import { startCase } from 'es-toolkit';
import { DecisionDetailStatusCode } from '../../Applicant/Applicant.types';
import { OpenConditionsFilter, ProductReviewType } from '@/components/shared/api/endpoints/applicant';
import { formatDate } from '@/components/shared/utils/utils';
import { OptionType } from '../index';
import { FilterCategory, FilterDefinition } from './types';

enum ProductCodes {
    ALL = 0,
    FACTORING,
    LOADPAY,
    TCSFUEL,
}

const PRODUCT_OPTION_LABELS: Record<string, string> = {
    LOADPAY: 'LoadPay',
    TCSFUEL: 'TCS Fuel Card',
};

enum PersonaCodes {
    CARRIER = 1,
    DRIVER = 2,
}

export const REVIEW_TYPE_OPTIONS: OptionType[] = [
    { name: 'LoadPay Only', code: ProductReviewType.LoadPay.toString() },
    { name: 'Factoring Only', code: ProductReviewType.Factoring.toString() },
    { name: 'Bundle', code: ProductReviewType.Bundle.toString() },
];

export const OPEN_CONDITIONS_OPTIONS: OptionType[] = [
    { name: 'Has open conditions', code: OpenConditionsFilter.HasOpen.toString() },
    { name: 'None open', code: OpenConditionsFilter.NoneOpen.toString() },
];

export const APPLICANT_STATUS_OPTIONS: OptionType[] = Object.entries(DecisionDetailStatusCode)
    .filter(([key]) => isNaN(parseInt(key)))
    .reduce<OptionType[]>((acc, [key, value]) => {
        if (value !== 0) {
            acc.push({ name: startCase(key), code: value.toString() });
        }
        return acc;
    }, []);

export const PRODUCT_OPTIONS: OptionType[] = Object.entries(ProductCodes)
    .filter(([key]) => isNaN(parseInt(key)))
    .reduce<OptionType[]>((acc, [key, value]) => {
        if (value !== 0) {
            acc.push({
                name: PRODUCT_OPTION_LABELS[key] ?? startCase(key),
                code: value.toString(),
            });
        }
        return acc;
    }, []);

// Assignee emails always contain '@', so this code cannot collide with one
export const UNASSIGNED_ASSIGNEE_CODE = 'unassigned';

export const UNASSIGNED_ASSIGNEE_OPTION: OptionType = {
    name: 'Unassigned',
    code: UNASSIGNED_ASSIGNEE_CODE,
};

export const PERSONA_OPTIONS: OptionType[] = [
    { name: 'Carrier', code: PersonaCodes.CARRIER.toString() },
    { name: 'Driver', code: PersonaCodes.DRIVER.toString() },
];

export const FILTER_CATEGORIES: readonly FilterCategory[] = [
    'APPLICATION',
    'CARRIER_IDENTITY',
    'CONTACT',
] as const;

export const FILTER_CATEGORY_LABELS: Record<FilterCategory, string> = {
    APPLICATION: 'Application',
    CARRIER_IDENTITY: 'Carrier Identity',
    CONTACT: 'Contact',
};

export const FILTER_REGISTRY: FilterDefinition[] = [
    {
        id: 'status',
        label: 'Status',
        description: 'Application status',
        category: 'APPLICATION',
        valueKind: 'multiselect',
        urlKey: 'status',
        defaultValue: [] as OptionType[],
        conflictsWith: ['reviewType'],
        isApplied: (v) => (v as OptionType[]).length > 0,
        chipLabel: (v) => {
            const val = v as OptionType[];
            return val.length > 0
                ? `Status: ${val.map((o) => o?.name).join(', ')}`
                : null;
        },
        serializeToParams: (params, v) => {
            (v as OptionType[]).forEach((o) => o && params.append('status', o.code));
        },
        parseFromParams: (params) => {
            return params
                .getAll('status')
                .map((code) => APPLICANT_STATUS_OPTIONS.find((opt) => opt?.code === code))
                .filter((opt): opt is NonNullable<typeof opt> => opt != null);
        },
        rendererProps: { options: APPLICANT_STATUS_OPTIONS },
    },
    {
        id: 'product',
        label: 'Product',
        description: 'Factoring, LoadPay, etc.',
        category: 'APPLICATION',
        valueKind: 'multiselect',
        urlKey: 'product',
        defaultValue: [] as OptionType[],
        conflictsWith: ['reviewType'],
        isApplied: (v) => (v as OptionType[]).length > 0,
        chipLabel: (v) => {
            const val = v as OptionType[];
            return val.length > 0
                ? `Product: ${val.map((o) => o?.name).join(', ')}`
                : null;
        },
        serializeToParams: (params, v) => {
            (v as OptionType[]).forEach((o) => o && params.append('product', o.code));
        },
        parseFromParams: (params) => {
            return params
                .getAll('product')
                .map((code) => PRODUCT_OPTIONS.find((opt) => opt?.code === code))
                .filter((opt): opt is NonNullable<typeof opt> => opt != null);
        },
        rendererProps: { options: PRODUCT_OPTIONS },
    },
    {
        id: 'persona',
        label: 'Persona',
        description: 'Carrier or Driver applicant',
        category: 'APPLICATION',
        valueKind: 'multiselect',
        urlKey: 'persona',
        defaultValue: [] as OptionType[],
        isApplied: (v) => (v as OptionType[]).length > 0,
        chipLabel: (v) => {
            const val = v as OptionType[];
            return val.length > 0
                ? `Persona: ${val.map((o) => o?.name).join(', ')}`
                : null;
        },
        serializeToParams: (params, v) => {
            (v as OptionType[]).forEach((o) => o && params.append('persona', o.code));
        },
        parseFromParams: (params) => {
            return params
                .getAll('persona')
                .map((code) => PERSONA_OPTIONS.find((opt) => opt?.code === code))
                .filter((opt): opt is NonNullable<typeof opt> => opt != null);
        },
        rendererProps: { options: PERSONA_OPTIONS },
    },
    {
        id: 'assignee',
        label: 'Assignee',
        description: 'Assigned reviewer',
        category: 'APPLICATION',
        valueKind: 'multiselect',
        urlKey: 'assignee',
        defaultValue: [] as OptionType[],
        isApplied: (v) => (v as OptionType[]).length > 0,
        chipLabel: (v) => {
            const val = v as OptionType[];
            return val.length > 0
                ? `Assignee: ${val.map((o) => o?.name).join(', ')}`
                : null;
        },
        serializeToParams: (params, v) => {
            (v as OptionType[]).forEach((o) => o && params.append('assignee', o.code));
        },
        parseFromParams: (params, ctx) => {
            const options = [UNASSIGNED_ASSIGNEE_OPTION, ...ctx.assigneeOptions];
            return params
                .getAll('assignee')
                .map((code) => options.find((opt) => opt?.code === code))
                .filter((opt): opt is NonNullable<typeof opt> => opt != null);
        },
    },
    {
        id: 'createdAtRange',
        label: 'Date Created',
        description: 'Range of application creation dates',
        category: 'APPLICATION',
        valueKind: 'date-range',
        urlKey: 'createdAtStart',
        urlKeys: ['createdAtStart', 'createdAtEnd'],
        defaultValue: null as (Date | null)[] | null,
        isApplied: (v) => {
            const range = v as (Date | null)[] | null;
            return range != null && range[0] != null && range[1] != null;
        },
        chipLabel: (v) => {
            const range = v as (Date | null)[] | null;
            if (!range || !range[0] || !range[1]) return null;
            return `Date Created: ${formatDate(range[0])} – ${formatDate(range[1])}`;
        },
        serializeToParams: (params, v) => {
            // Serialize local-day boundaries as absolute UTC timestamps. The
            // DateRangeField snaps to local 00:00 / 23:59:59.999 before this
            // runs, so a "Jan 15 – Jan 20" range always covers the reviewer's
            // full calendar days regardless of timezone.
            const range = v as (Date | null)[] | null;
            if (!range?.[0] || !range?.[1]) return;
            params.set('createdAtStart', range[0].toISOString());
            params.set('createdAtEnd', range[1].toISOString());
        },
        parseFromParams: (params) => {
            // Require both ends to be present and parseable. A URL with only
            // one end is treated as no filter
            const startParam = params.get('createdAtStart');
            const endParam = params.get('createdAtEnd');
            if (!startParam || !endParam) return null;
            const start = new Date(startParam);
            const end = new Date(endParam);
            if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
            return [start, end];
        },
    },
    {
        id: 'usDot',
        label: 'US DOT',
        description: 'US DOT number',
        category: 'CARRIER_IDENTITY',
        valueKind: 'digits-text',
        urlKey: 'usDot',
        defaultValue: '',
        isApplied: (v) => (v as string).length > 0,
        chipLabel: (v) => {
            const val = v as string;
            return val.length > 0 ? `US DOT: ${val}` : null;
        },
        serializeToParams: (params, v) => {
            const val = v as string;
            if (val) params.set('usDot', val);
        },
        parseFromParams: (params) => {
            return (params.get('usDot') ?? '').replace(/\D/g, '');
        },
    },
    {
        id: 'businessNames',
        label: 'Business Name',
        description: 'Legal or DBA business name',
        category: 'CARRIER_IDENTITY',
        valueKind: 'chips',
        urlKey: 'businessName',
        defaultValue: [] as string[],
        isApplied: (v) => (v as string[]).length > 0,
        chipLabel: (v) => {
            const val = v as string[];
            if (val.length === 0) return null;
            return `Business Name: ${val.length === 1 ? val[0] : `${val.length} values`}`;
        },
        serializeToParams: (params, v) => {
            (v as string[]).forEach((name) => params.append('businessName', name));
        },
        parseFromParams: (params) => {
            return params.getAll('businessName').filter((n) => n.length > 0);
        },
    },
    {
        id: 'ownerNames',
        label: 'Owner Name',
        description: 'Any owner or controlling manager',
        category: 'CARRIER_IDENTITY',
        valueKind: 'chips',
        urlKey: 'ownerName',
        defaultValue: [] as string[],
        isApplied: (v) => (v as string[]).length > 0,
        chipLabel: (v) => {
            const val = v as string[];
            if (val.length === 0) return null;
            return `Owner Name: ${val.length === 1 ? val[0] : `${val.length} values`}`;
        },
        serializeToParams: (params, v) => {
            (v as string[]).forEach((n) => params.append('ownerName', n));
        },
        parseFromParams: (params) => {
            return params.getAll('ownerName').filter((n) => n.length > 0);
        },
    },
    {
        id: 'mcNumbers',
        label: 'MC Number',
        description: 'MC / docket number',
        category: 'CARRIER_IDENTITY',
        valueKind: 'chips',
        urlKey: 'mcNumber',
        defaultValue: [] as string[],
        isApplied: (v) => (v as string[]).length > 0,
        chipLabel: (v) => {
            const val = v as string[];
            if (val.length === 0) return null;
            return `MC Number: ${val.length === 1 ? val[0] : `${val.length} values`}`;
        },
        serializeToParams: (params, v) => {
            (v as string[]).forEach((n) => params.append('mcNumber', n));
        },
        parseFromParams: (params) => {
            return params.getAll('mcNumber').filter((n) => n.length > 0);
        },
    },
    {
        id: 'feins',
        label: 'FEIN',
        description: 'Federal Employer Identification Number',
        category: 'CARRIER_IDENTITY',
        valueKind: 'chips',
        urlKey: 'fein',
        defaultValue: [] as string[],
        isApplied: (v) => (v as string[]).length > 0,
        chipLabel: (v) => {
            const val = v as string[];
            if (val.length === 0) return null;
            return `FEIN: ${val.length === 1 ? val[0] : `${val.length} values`}`;
        },
        serializeToParams: (params, v) => {
            (v as string[]).forEach((n) => params.append('fein', n));
        },
        parseFromParams: (params) => {
            return params.getAll('fein').filter((n) => n.length > 0);
        },
    },
    {
        id: 'emails',
        label: 'Email',
        description: 'Any email on the application',
        category: 'CONTACT',
        valueKind: 'chips',
        urlKey: 'email',
        defaultValue: [] as string[],
        isApplied: (v) => (v as string[]).length > 0,
        chipLabel: (v) => {
            const val = v as string[];
            if (val.length === 0) return null;
            return `Email: ${val.length === 1 ? val[0] : `${val.length} values`}`;
        },
        serializeToParams: (params, v) => {
            (v as string[]).forEach((e) => params.append('email', e));
        },
        parseFromParams: (params) => {
            return params.getAll('email').filter((e) => e.length > 0);
        },
    },
    {
        id: 'phones',
        label: 'Phone Number',
        description: 'Any phone number on the application',
        category: 'CONTACT',
        valueKind: 'chips',
        urlKey: 'phone',
        defaultValue: [] as string[],
        isApplied: (v) => (v as string[]).length > 0,
        chipLabel: (v) => {
            const val = v as string[];
            if (val.length === 0) return null;
            return `Phone: ${val.length === 1 ? val[0] : `${val.length} values`}`;
        },
        serializeToParams: (params, v) => {
            (v as string[]).forEach((p) => params.append('phone', p));
        },
        parseFromParams: (params) => {
            return params.getAll('phone').filter((p) => p.length > 0);
        },
    },
    {
        id: 'reviewType',
        label: 'Review Type',
        description: 'Filter by product review queue (mutually exclusive with Status and Product)',
        category: 'APPLICATION',
        valueKind: 'dropdown',
        urlKey: 'reviewType',
        defaultValue: null as OptionType,
        conflictsWith: ['status', 'product'],
        isApplied: (v) => v !== null,
        chipLabel: (v) => {
            const val = v as OptionType;
            return val ? `Review Type: ${val.name}` : null;
        },
        serializeToParams: (params, v) => {
            const val = v as OptionType;
            if (val) params.set('reviewType', val.code);
        },
        parseFromParams: (params) => {
            const code = params.get('reviewType');
            if (!code) return null;
            return REVIEW_TYPE_OPTIONS.find((opt) => opt && opt.code === code) ?? null;
        },
        rendererProps: { options: REVIEW_TYPE_OPTIONS },
    },
    {
        id: 'openConditions',
        label: 'Open Conditions',
        description: 'Applications with or without open conditions on the active product(s)',
        category: 'APPLICATION',
        valueKind: 'dropdown',
        urlKey: 'openConditions',
        defaultValue: null as OptionType,
        isApplied: (v) => v !== null,
        chipLabel: (v) => {
            const val = v as OptionType;
            return val ? `Open Conditions: ${val.name}` : null;
        },
        serializeToParams: (params, v) => {
            const val = v as OptionType;
            if (val) params.set('openConditions', val.code);
        },
        parseFromParams: (params) => {
            const code = params.get('openConditions');
            if (!code) return null;
            return OPEN_CONDITIONS_OPTIONS.find((opt) => opt && opt.code === code) ?? null;
        },
        rendererProps: { options: OPEN_CONDITIONS_OPTIONS },
    },
];

export function getFilterById(id: string): FilterDefinition | undefined {
    return FILTER_REGISTRY.find((f) => f.id === id);
}

export interface FilterDisabledInfo {
    disabled: boolean;
    reason: string | null;
}

/**
 * Returns whether `filter` should be disabled in the picker because one or
 * more of its `conflictsWith` filters is currently in `picked`, along with a
 * human-readable reason suitable for a tooltip.
 */
export function getFilterDisabledInfo(
    filter: FilterDefinition,
    picked: Set<string>,
): FilterDisabledInfo {
    const blockers = (filter.conflictsWith ?? []).filter((id) => picked.has(id));
    if (blockers.length === 0) return { disabled: false, reason: null };

    const blockerLabels = blockers
        .map((id) => FILTER_REGISTRY.find((f) => f.id === id)?.label ?? id)
        .join(' and ');
    return {
        disabled: true,
        reason: `Clear ${blockerLabels} to use this filter`,
    };
}

